import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Empty, Input, Pagination, Skeleton, message } from 'antd';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FeaturePage from '@/components/featurepage';
import { fetchPostTags, getPostErrorMessage } from '@/apis/posts';
import { fetchMyTags, addMyTag, deleteMyTag } from '@/apis/account';
import './index.scss';
import PullToRefresh from '@/components/pulltorefresh';
import useMobile from '@/utils/useMobile';

const categories = { study: '学习', sports: '运动', life: '生活', skill: '技能', other: '其他' };
const MyTags = () => {
  const { isAuthenticated } = useSelector(state => state.user);
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [search, setSearch] = useState(''); const [page, setPage] = useState(1); const [pagination, setPagination] = useState(null);
  const mobile = useMobile(); const version = useRef(0);
  useEffect(() => { const timer = setTimeout(() => { setSearch(query.trim()); setPage(1); }, 300); return () => clearTimeout(timer); }, [query]);
  const busyRef = useRef(false);
  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    const current = ++version.current; setLoading(true); setError('');
    try {
      const [all, mine] = await Promise.all([fetchPostTags({ paginate: true, q: search, page, pageSize: 50 }), fetchMyTags()]);
      if (!Array.isArray(all?.data) || !Array.isArray(mine)) throw new Error('标签响应无效');
      if (current === version.current) { setTags(all.data); setPagination(all.pagination); setSelected(mine.map(item => item.tag.id)); }
    } catch (err) { const text = getPostErrorMessage(err, '标签加载失败'); if (current === version.current) setError(text); throw new Error(text); }
    finally { if (current === version.current) setLoading(false); }
  }, [isAuthenticated, page, search]);
  useEffect(() => { load().catch(() => {}); return () => { version.current += 1; }; }, [load, retry]);
  const toggle = async id => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(id);
    const removing = selected.includes(id);
    try {
      if (removing) await deleteMyTag(id); else await addMyTag(id);
      setSelected(current => removing ? current.filter(value => value !== id) : [...current, id]);
      message.success(removing ? '已移除兴趣标签' : '已添加兴趣标签');
    } catch (err) { message.error(getPostErrorMessage(err, '标签保存失败')); }
    finally { busyRef.current = false; setBusy(null); }
  };
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FeaturePage embedded showDesktopBack={false} className="myTags" title="兴趣标签" subtitle="选择你的兴趣，告诉搭子你喜欢做什么。">
    <PullToRefresh enabled={mobile} disabled={loading || busy !== null} onRefresh={load} successMessage="标签已刷新"><section className="featurePage-panel">
      <Input.Search maxLength={30} placeholder="搜索标签" value={query} onChange={event => setQuery(event.target.value)} allowClear />
      <p className="myTags-count">已选 {selected.length} 个 · 点击标签即可添加或移除</p>
      {loading ? <Skeleton active /> : error ? <Alert type="error" showIcon message={error} action={<Button onClick={() => setRetry(value => value + 1)}>重试</Button>} />
        : tags.length === 0 ? <Empty description="暂无可选标签" /> : <>
          {Object.entries(categories).map(([key, label]) => {
            const list = tags.filter(tag => tag.category === key);
            return list.length > 0 && <section key={key} className="myTags-group"><h2>{label}</h2><div>
              {list.map(tag => <Button key={tag.id} type={selected.includes(tag.id) ? 'primary' : 'default'} aria-pressed={selected.includes(tag.id)} loading={busy === tag.id} disabled={busy !== null && busy !== tag.id} onClick={() => toggle(tag.id)}>{selected.includes(tag.id) ? '✓ ' : '+ '}{tag.name}</Button>)}
            </div></section>;
          })}
        </>}
      {!loading && !error && pagination?.total > 50 && <Pagination current={page} pageSize={50} total={pagination.total} onChange={setPage} showSizeChanger={false} />}
    </section></PullToRefresh>
  </FeaturePage>;
};
export default MyTags;
